import { Heading, LinkButton, Paragraph, Picture, RichText } from 'ui/components';
import styles from './ProductTabsContent.module.scss';
import IconArrowRight from 'ui/icons/icon-arrow-right-web.svg';
import classNames from 'classnames';

type QuadrantItem = {
	position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
	title?: string;
	description?: string;
	image?: {
		url?: string;
		altText?: string;
		focalPoint?: { left: number; top: number };
	};
};

type QuadrantsPayload = {
	heading?: string;
	subheading?: string;
	disclaimer?: string;
	items: QuadrantItem[];
};

export type ProductTabsContentProps = {
	id: string;
	isActive?: string;
	kind: 'standard' | 'quadrants';
	// Standard
	text?: string;
	image?: {
		url?: string;
		altText?: string;
		focalPoint?: { left: number; top: number };
	};
	// Quadrants
	dataSheetTitle?: string;
	dataSheetLink?: { url: string; name?: string; target?: string };
	quadrants?: QuadrantsPayload;
};

function QuadrantsPanel({ quadrants }: { quadrants: QuadrantsPayload }) {
	return (
		<div className={styles.Quadrants}>
			{(quadrants?.heading || quadrants?.subheading) && (
				<div className={styles.Quadrants_header}>
					{quadrants.heading && (
						<Heading headingLevel="h2" style="displaySm" className={styles.Quadrants_heading}>
							{quadrants?.heading}
						</Heading>
					)}
					{quadrants?.subheading && (
						<Paragraph className={styles.Quadrants_subheading} style="md">
							{quadrants?.subheading}
						</Paragraph>
					)}
				</div>
			)}

			<div className={styles.ProductTabsContent_QuadrantsGrid}>
				{quadrants?.items.map((quadrantItem, i) => (
					<div key={i} className={classNames(styles.ProductTabsContent_QuadrantsItem, styles[quadrantItem?.position])}>
						{quadrantItem?.image?.url && <Picture url={quadrantItem?.image.url} aspectRatio={1} className={styles.Quadrants_image} />}
						<div className={styles.ProductTabsContent_QuadrantsItemGrid}>
							{quadrantItem?.title && (
								<Heading headingLevel="h3" style="sm" className={styles.Quadrants_title}>
									{quadrantItem?.title}
								</Heading>
							)}
							{quadrantItem?.description && <RichText className={styles.Quadrants_desc} content={quadrantItem?.description} />}
						</div>
					</div>
				))}
			</div>

			{quadrants.disclaimer && (
				<Paragraph className={styles.ProductTabsContent_disclaimer} style="sm">
					{quadrants.disclaimer}
				</Paragraph>
			)}
		</div>
	);
}

export const ProductTabsContent: React.FC<ProductTabsContentProps> = (tabContent) => {
	const isActiveThis = tabContent?.isActive === tabContent?.id;
	if (!isActiveThis) return null;

	if (tabContent?.kind === 'quadrants') {
		// Quadrant
		return (
			<div
				className={classNames(styles.ProductTabsContent, styles.isActive)}
				role="tabpanel"
				id={`panel-${tabContent?.id}`}
				aria-labelledby={`tab-${tabContent?.id}`}
			>
				{tabContent?.quadrants && <QuadrantsPanel quadrants={tabContent?.quadrants} />}
			</div>
		);
	}

	// Standard
	return (
		<div
			className={classNames(styles.ProductTabsContent, styles.isActive)}
			role="tabpanel"
			id={`panel-${tabContent?.id}`}
			aria-labelledby={`tab-${tabContent?.id}`}
		>
			<div className={styles.ProductTabsContent_textWrapper}>
				{tabContent?.text && (
					<Heading headingLevel="h2" style="displaySm" className={styles.ProductTabsContent_tabcontentHeading}>
						{tabContent?.dataSheetTitle}
					</Heading>
				)}

				{tabContent?.dataSheetTitle && (
					<RichText content={tabContent?.text} variant="website" className={styles.ProductTabsContent_tabcontentText} />
				)}

				{tabContent?.dataSheetLink?.url && (
					<LinkButton
						withBackIcon={<IconArrowRight />}
						className={styles.ProductDetails_submitButton}
						style="primaryWeb"
						target={tabContent?.dataSheetLink.target}
						url={tabContent?.dataSheetLink.url}
					>
						{tabContent?.dataSheetLink.name}
					</LinkButton>
				)}
			</div>

			<div className={styles.ProductTabsContent_imageWrapper}>
				{tabContent?.image?.url && <Picture url={tabContent?.image.url} aspectRatio={0.75} className={styles.ProductTabsContent_image} />}
			</div>
		</div>
	);
};
